// The completeness score (Module 02 §4 S1), restyled from the 88 px SVG dial
// to the register's instrument grammar (design-system §3.4, UX-1): a compact
// stat block — 11 px uppercase label, 20–24 px tabular value, a 4 px meter on
// the track — plus the threshold/blocking captions. NEVER a poster numeral.
//
// The score is `round(100 × Σ weight(passed ∪ waived) / Σ weight(all))` over
// the CLOSED 19-item catalog (§3.6). A never-evaluated tenant scores 0, and
// this says "not evaluated yet" rather than implying 0 % done.
//
// Pure presentation: the caller supplies the numbers. The `score-dial` testid
// and `data-score` attribute are STABLE walkthrough API (kept through UX-1).

import { useTranslation } from "react-i18next";

export interface ScoreDialProps {
  /** 0–100, from `checklist_summary.score` / the checklist response. */
  score: number;
  /** The go-live gate (85). Rendered as the "needed to go live" caption. */
  threshold: number;
  /** Blocking item keys not passed/waived — the count under the value. */
  blockingUnmet: number;
  /** True when the checklist has never been evaluated (score 0 by absence). */
  neverEvaluated?: boolean;
  /** Compact variant (meta rows dropped) for tight headers. */
  compact?: boolean;
}

export function ScoreDial({
  score,
  threshold,
  blockingUnmet,
  neverEvaluated = false,
  compact = false,
}: ScoreDialProps) {
  const { t } = useTranslation();
  const clamped = Math.max(0, Math.min(100, Math.round(score)));
  // Semantic tone (§1 rule 3): green only at/above the gate, amber when work
  // remains, neutral before anything happened. Carried as a class on the block
  // and its meter — never a poster arc.
  const tone = clamped >= threshold ? "ok" : clamped > 0 ? "warn" : "neutral";

  return (
    <div
      className={`score-block score-block-${tone}${compact ? " score-block-compact" : ""}`}
      data-testid="score-dial"
      data-score={clamped}
    >
      <p className="score-block-label">{t("onboarding.score.label")}</p>
      <p
        className="score-block-value num"
        aria-label={t("onboarding.score.value", { score: clamped })}
      >
        {clamped}
        <span className="score-block-unit">%</span>
      </p>
      <div className="ds-mbar" aria-hidden="true">
        <i style={{ width: `${clamped}%` }} />
      </div>
      {!compact ? (
        <>
          {neverEvaluated ? (
            <p className="score-block-note">{t("onboarding.score.neverEvaluated")}</p>
          ) : (
            <p className="score-block-note">{t("onboarding.score.threshold", { threshold })}</p>
          )}
          <p className={`score-block-note${blockingUnmet > 0 ? " is-blocking" : ""}`}>
            {blockingUnmet > 0
              ? t("onboarding.score.blockingUnmet", { count: blockingUnmet })
              : t("onboarding.score.blockingClear")}
          </p>
        </>
      ) : null}
    </div>
  );
}
